import { Component, ElementRef, inject, ViewChild, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { LoggingService } from '../../shared/services/logging';
import { StateService, UIRouter } from '@uirouter/core';
import { Store } from '@ngrx/store';
import { AppComponent } from '../../app.component';
import * as fromAuth from '../../auth/store/auth.reducer';
import * as fromUser from '../../user-info/store/user.reducer';
import { UIRouterModule } from '@uirouter/angular';
import { LayoutService } from '../service/layout.service';
import { MenuService } from '../service/menu.service';
import { MessageStateService } from '../../shared/services/message-state.service';
import { ReplaySubject, Subscription } from 'rxjs';
import { IUserRole } from '../../user-role';
import * as fromUserInfo from '../../user-info';

@Component({
    selector: '[app-menu]',
    templateUrl: './app.menu.html',
    styleUrls: ['./app.menu.scss'],
    standalone: true,
    imports: [CommonModule, AppMenuitem, UIRouterModule],
    // template: `<ul class="layout-menu" #menuContainer>
    //     <ng-container *ngFor="let item of model; let i = index">
    //         <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
    //         <li *ngIf="item.separator" class="menu-separator"></li>
    //     </ng-container>
    // </ul> `
})
export class AppMenu implements OnInit, OnDestroy {
    el = inject(ElementRef);

    @ViewChild('menuContainer') menuContainer!: ElementRef;

    private menuSubscription: Subscription;
    private menuUpdateSubscription: Subscription;
    private messageStateSubscription: Subscription;


    private RoleDrivenModelSubject = new ReplaySubject<Array<any>>(1);
    private _roledrivenmodel: Array<any>;
    @Input() set roleDrivenModel(value: Array<any>) {
        if (value) {
            this._roledrivenmodel = value;
            this.RoleDrivenModelSubject.next(value);
            this.slimModel = this._roledrivenmodel;
            this.updateAiExpertMenuVisibility();
            this.menuService.slimModel = this.slimModel;
        }
    }
    get roleDrivenModel(): Array<any> {
        return this._roledrivenmodel;
    }

    constructor(
            public app: AppComponent,
            public layoutService: LayoutService,
            private store: Store<fromUser.State>,
            private authStore: Store<fromAuth.State>,
            private ss: StateService,
            private log: LoggingService,
            private menuService: MenuService,
            private messageStateService: MessageStateService
        ) {


        }

    ngOnInit() {
        // Initialize the menu data with current app state
        this.updateMessageBadge();
        this.updateCheckupBadge();

        // Sync the menu data with the service
        this.menuService.model = this.model;
        this.menuService.slimModel = this.slimModel;

        // Subscribe to menu changes and update local models
        this.menuSubscription = this.menuService.slimModel$.subscribe(updatedSlimModel => {
            this.slimModel = updatedSlimModel;
        });

        // Subscribe to manual update triggers
        this.menuUpdateSubscription = this.menuService.menuUpdateTrigger$.subscribe(() => {
            this.updateMessageBadge();
            this.updateCheckupBadge();
            this.updateAiExpertMenuVisibility();
            this.menuService.slimModel = this.slimModel;
            this.menuService.model = this.model;
        });

        // Subscribe to message state changes for reactive badge updates
        this.messageStateSubscription = this.messageStateService.showBadge$.subscribe(showBadge => {
            const messageCount = this.messageStateService.messageCount;
            this.setBadge(messageCount, showBadge);
        });

        // Subscribe to changes in the checkup process badge (polling or event-based)
        // If eFCheckupProcess is updated via an observable/event, subscribe here instead
        setInterval(() => {
            this.updateCheckupBadge();
        }, 10000); // Poll every 10 seconds (adjust as needed)
    }
    updateCheckupBadge() {
        // Defensive: app.eFCheckupProcess may not be set yet
        const checkupProcess = this.app.eFCheckupProcess;
        let badgeValue = '';
        let badgeClass = 'ef-menuitem-checkup-badge-off';
        if (checkupProcess && typeof checkupProcess.MsgCount === 'number' && checkupProcess.MsgCount > 0) {
            badgeValue = checkupProcess.MsgCount.toString();
            badgeClass = 'ef-menuitem-checkup-badge-on';
        }

        // Update in slimModel
        const checkupIndex = this.slimModel.findIndex(item => item.label === 'eFAACT Checkup');
        if (checkupIndex !== -1) {
            this.slimModel[checkupIndex] = {
                ...this.slimModel[checkupIndex],
                badge: badgeValue,
                badgeStyleClass: badgeClass
            };
        }

        // Update in model (nested in Quick Menu)
        for (let i = 0; i < this.model.length; i++) {
            if (this.model[i].items) {
                const itemIndex = this.model[i].items.findIndex(item => item.label === 'eFAACT Checkup');
                if (itemIndex !== -1) {
                    this.model[i].items[itemIndex] = {
                        ...this.model[i].items[itemIndex],
                        badge: badgeValue,
                        badgeStyleClass: badgeClass
                    };
                    break;
                }
            }
        }
    }

    updateMessageBadge() {
        // Get the current state from the message service
        const messageCount = this.messageStateService.messageCount;
        const showBadge = this.messageStateService.showBadge;
        this.setBadge(messageCount, showBadge);
        console.log('updateMessageBadge called - count:', messageCount, 'showBadge:', showBadge);
    }

    setBadge(messageCount: number, showBadge: boolean = true) {
        const badgeValue = messageCount > 0 && showBadge ? messageCount.toString() : '';
        const badgeClass = messageCount > 0 && showBadge ? 'ef-menuitem-badge-on' : 'ef-menuitem-badge-off';

        // Update the Messages item in slimModel
        const messageIndex = this.slimModel.findIndex(item => item.label === 'Messages');
        if (messageIndex !== -1) {
            this.slimModel[messageIndex] = {
                ...this.slimModel[messageIndex],
                badge: badgeValue,
                badgeStyleClass: badgeClass
            };
            console.log('Updated slimModel Messages item:', this.slimModel[messageIndex]);
        }

        // Update the Messages item in model (nested in Quick Menu)
        for (let i = 0; i < this.model.length; i++) {
            if (this.model[i].items) {
                const itemIndex = this.model[i].items.findIndex(item => item.label === 'Messages');
                if (itemIndex !== -1) {
                    this.model[i].items[itemIndex] = {
                        ...this.model[i].items[itemIndex],
                        badge: badgeValue,
                        badgeStyleClass: badgeClass
                    };
                    console.log('Updated model Messages item:', this.model[i].items[itemIndex]);
                    break;
                }
            }
        }
    }

    updateAiExpertMenuVisibility() {
        const visible = this.app.showAiExpertSidebarButton;
        const menuId = this.app.aiExpertMenuItemId;

        const slimIndex = this.slimModel.findIndex(
            item => item.id === menuId || item.label === 'Ask eFAACT'
        );
        if (slimIndex !== -1) {
            this.slimModel[slimIndex] = {
                ...this.slimModel[slimIndex],
                class: 'ai-expert-menuitem',
                visible
            };
        }

        for (let i = 0; i < this.model.length; i++) {
            if (this.model[i].items) {
                const itemIndex = this.model[i].items.findIndex(
                    item => item.id === menuId || item.label === 'Ask eFAACT'
                );
                if (itemIndex !== -1) {
                    this.model[i].items[itemIndex] = {
                        ...this.model[i].items[itemIndex],
                        class: 'ai-expert-menuitem',
                        visible
                    };
                    break;
                }
            }
        }
    }

    ngOnDestroy() {
        if (this.menuSubscription) {
            this.menuSubscription.unsubscribe();
        }
        if (this.menuUpdateSubscription) {
            this.menuUpdateSubscription.unsubscribe();
        }
        if (this.messageStateSubscription) {
            this.messageStateSubscription.unsubscribe();
        }
    }

    model: any[] = [
        {
            label: 'Quick Menu',
            icon: 'pi pi-fw pi-objects-column',
            items: [
                {
                    label: 'Dashboard',
                    icon: 'pi pi-fw pi-objects-column',
                    command: event => {
                        this.app.onMenuDashboardClick(event);
                    }
                    //uiSref: 'dashboard'
                },
                {
                    label: 'Home',
                    icon: 'pi pi-fw pi-home',
                    command: event => {
                        this.app.onMenuHomeClick(event);
                    }
                    //uiSref: 'main'
                },
                // {
                //     label: 'Reports',
                //     icon: ' far fa-file-alt',
                //     command: event => {
                //         this.app.onReportButtonClick(event);
                //         if (this.app.DetailSideNavVisible == true) {
                //             this.app.DetailSideNavVisible =
                //                 !this.app.DetailSideNavVisible;
                //         }
                //         if (this.app.MessageSideNavVisible == true) {
                //             this.app.MessageSideNavVisible =
                //                 !this.app.MessageSideNavVisible;
                //         }
                //     }
                // },
                {
                    label: 'Company Calendar',
                    icon: 'pi pi-fw pi-calendar',
                    command: event => {
                        this.app.onTopbarCompanyCalendarClick(event);
                        if (this.app.DetailSideNavVisible == true) {
                            this.app.DetailSideNavVisible =
                                !this.app.DetailSideNavVisible;
                        }
                        if (this.app.MessageSideNavVisible == true) {
                            this.app.MessageSideNavVisible =
                                !this.app.MessageSideNavVisible;
                        }
                    }
                },
                {
                    label: 'eFAACT Checkup',
                    icon: 'pi pi-fw pi-exclamation-triangle',
                    badge: '',
                    badgeStyleClass: 'ef-menuitem-checkup-badge-off',
                    command: event => {
                        this.app.onMenuEFCheckupClick(event);
                        if (this.app.DetailSideNavVisible == true) {
                            this.app.DetailSideNavVisible =
                                !this.app.DetailSideNavVisible;
                        }
                        if (this.app.MessageSideNavVisible == true) {
                            this.app.MessageSideNavVisible =
                                !this.app.MessageSideNavVisible;
                        }
                    }
                },
                {
                    label: 'Messages',
                    icon: 'pi pi-fw pi-envelope',
                    badge: '',
                    badgeStyleClass: 'ef-menuitem-badge-off',
                    command: event => {
                        if (this.app.DetailSideNavVisible == true) {
                            this.app.DetailSideNavVisible =
                                !this.app.DetailSideNavVisible;
                        }
                        this.app.MessageSideNavVisible =
                            !this.app.MessageSideNavVisible;
                    }
                },
                {
                    label: 'Company Settings',
                    icon: 'pi pi-fw pi-cog',
                    command: event => {
                        this.app.onSidebarCompanySettingsClick();
                        if (this.app.MessageSideNavVisible == true) {
                            this.app.MessageSideNavVisible =
                                !this.app.MessageSideNavVisible;
                        }
                        this.app.DetailSideNavVisible =
                            !this.app.DetailSideNavVisible;
                    }
                },
                {
                    id: 'ai-expert',
                    label: 'Ask eFAACT',
                    icon: 'pi pi-sparkles',
                    class: 'ai-expert-menuitem',
                    visible: false,
                    command: event => {
                        this.app.openFloatingExpertChatFromSidebar(
                            event?.originalEvent
                        );
                    }
                }
            ]
        }
    ];

    slimModel: any[] = [];

    get isSlim() {
        return this.layoutService.isSlim();
    }
}
